import API from '../apiCreate'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { getParams } from '../getParams'
import { ResponseNumber, ResponseWrapper } from '../types/base.types'
import {
  RequestChangeLicense,
  RequestRenewLicense,
  ResponseLicenseAndPayment,
  ResponsePayment,
  ResponseMetricsGraduationData,
  ResponseGetPriceParam,
  ResponseGetPrice,
  RequestGetPriceParam,
  ResponsePaymentHistory,
  LicenseCompanyDetails,
  ResponseCompanyDetails,
  CompanyLookupResult,
  BankLookupResult,
  ResponseBillingDocuments,
  ResponseCreateInvoice,
  LicenseReferralData,
  // RequestPriceForPayment,
} from '../types/licenseAndPayment.types'

// Share only requests that are still running. Never cache balances or limits
// after a response, and never share a request between authenticated sessions.
const pendingDataRequests = new Map<string, Promise<AxiosResponse<ResponseLicenseAndPayment>>>()

export const licenseAndPaymentApi = {
  getData: () => {
    const session = localStorage.getItem('userToken') ?? ''
    const pending = pendingDataRequests.get(session)
    if (pending) return pending

    const request = API.get<ResponseLicenseAndPayment>('license-and-payment/get-data', {
      params: { sync_payments: 0 },
    })
    pendingDataRequests.set(session, request)
    const release = () => { pendingDataRequests.delete(session) }
    request.then(release, release)
    return request
  },

  syncPayments: () =>
    API.post<ResponseWrapper<{ changed: boolean }>>('license-and-payment/sync-payments'),

  getReferrals: (page = 1, deductionPage = 1) =>
    API.get<ResponseWrapper<LicenseReferralData>>(
      'license-and-payment/get-referrals',
      { params: { page, deduction_page: deductionPage, payout_page: deductionPage } },
    ),

  getPaymentHistory: () =>
    API.get<ResponsePaymentHistory>('license-and-payment/get-payment-history'),

  getCompanyDetails: () =>
    API.get<ResponseCompanyDetails>('license-and-payment/get-company-details'),

  saveCompanyDetails: (data: LicenseCompanyDetails) =>
    API.post<ResponseCompanyDetails>(
      'license-and-payment/save-company-details',
      data,
      { skipGlobalErrorAlert: true } as AxiosRequestConfig & { skipGlobalErrorAlert: boolean },
    ),

  lookupCompany: (inn: string) =>
    API.post<ResponseWrapper<CompanyLookupResult>>(
      'license-and-payment/lookup-company',
      { inn },
    ),

  lookupBank: (bik: string) =>
    API.post<ResponseWrapper<BankLookupResult>>(
      'license-and-payment/lookup-bank',
      { bik },
    ),

  getBillingDocuments: () =>
    API.get<ResponseBillingDocuments>(
      'license-and-payment/get-billing-documents',
    ),

  createInvoice: (
    data: Pick<
      RequestRenewLicense,
      'metrics' | 'months' | 'video_rooms_composition' | 'request_key' | 'referral_amount' | 'amount'
    > | (RequestChangeLicense & { operation_type: 'parameters' }),
  ) =>
    API.post<ResponseCreateInvoice>('license-and-payment/create-invoice', data),

  downloadInvoice: (id: number) =>
    API.get<Blob>(`license-and-payment/download-invoice?id=${id}`, {
      responseType: 'blob',
    }),

  downloadAct: (id: number) =>
    API.get<Blob>(`license-and-payment/download-act?id=${id}`, {
      responseType: 'blob',
    }),

  getMetricsGraduationData: () =>
    API.get<ResponseMetricsGraduationData>(
      'license-and-payment/get-metrics-graduation-data',
    ),

  // FIXME: удалить цену из payload (цена должна считаться на бэке)
  renewLicense: (data: RequestRenewLicense) =>
    API.post<ResponsePayment>('license-and-payment/renew-license', data),

  changeLicense: (data: RequestChangeLicense) =>
    API.post<ResponsePayment>('license-and-payment/change-license', data),

  // deprecated
  // getPriceForPayment: (data: RequestPriceForPayment) =>
  //   API.post<ResponseNumber>('license-and-payment/get-price-for-payment', data),

  // используется для получения цены при renewLicense
  getPrice: (
    data: Pick<
      RequestRenewLicense,
      'metrics' | 'months' | 'video_rooms_composition'
    >,
  ) =>
    API.post<ResponseGetPrice>('license-and-payment/get-price', data),

  // используется для получения цены при changeLicense
  getPriceParam: (data: RequestGetPriceParam) =>
    API.post<ResponseGetPriceParam>(
      'license-and-payment/get-price-param',
      data,
    ),

  checkPayment: (params: { id: number }) =>
    API.get<ResponseNumber>( // response data =    1 = 'success' | 0 = 'process'
      `license-and-payment/check-payment/${getParams(params)}`,
    ),
}
